// Listings.jsx
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Components/NavBar";
import "../css/LandingPage.css";
import "../css/listings.css";
import brickFooter from "../css/images/brick_footer.png";

const ZILLOW_PROFILE_URL = "https://www.zillow.com/profile/broscoehome";

// Snapshot of the last listings payload so repeat visits paint instantly
// while we revalidate against the API in the background.
const LISTINGS_CACHE_KEY = "khl-listings-cache-v1";

function readListingsSnapshot() {
  try {
    const cached = JSON.parse(localStorage.getItem(LISTINGS_CACHE_KEY) || "null");
    if (cached && (Array.isArray(cached.active) || Array.isArray(cached.sold))) {
      return { active: cached.active || [], sold: cached.sold || [] };
    }
  } catch {}
  return null;
}

function writeListingsSnapshot(data) {
  try {
    localStorage.setItem(LISTINGS_CACHE_KEY, JSON.stringify(data));
  } catch {}
}

const normStatus = (s) =>
  String(s || "active")
    .toLowerCase()
    .replace(/\u00a0/g, " ")
    .trim();

function StatusRibbon({ status }) {
  const s = normStatus(status);
  const label = s === "sold" ? "Sold" : "Active";
  return <div className={`listing-ribbon ${s}`}>{label}</div>;
}

function JumpChip({ href, children }) {
  return (
    <a className="listing-chip" href={href}>
      {children}
    </a>
  );
}

function ListingCard({ item, showPrice = true, priority = false }) {
  const priceNumber = item?.price
    ? Number(String(item.price).replace(/[^\d]/g, ""))
    : null;

  const address = item?.address || "Property";
  const location = [item?.city, item?.state, item?.zip]
    .filter(Boolean)
    .join(", ")
    .replace(/, (\d{5})$/, " $1"); // turn "... , 12345" into "... 12345"

  return (
    <article className="listing-card ui-card">
      <StatusRibbon status={item?.status} />

      <div className="listing-media">
        <img
          src={item?.image}
          alt={address}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
        />
        <div className="listing-media-overlay" />
      </div>

      <div className="listing-body">
        <h3 className="listing-title">{address}</h3>
        {location ? <p className="listing-location">{location}</p> : null}

        <div className="listing-meta">
          {item?.beds ? (
            <span className="meta-pill">
              <strong>{item.beds}</strong> bd
            </span>
          ) : null}
          {item?.baths ? (
            <span className="meta-pill">
              <strong>{item.baths}</strong> ba
            </span>
          ) : null}
          {item?.sqft ? (
            <span className="meta-pill">
              <strong>{Number(item.sqft).toLocaleString()}</strong> sqft
            </span>
          ) : null}
        </div>

        {showPrice && priceNumber ? (
          <p className="listing-price">${priceNumber.toLocaleString()}</p>
        ) : null}

        {item?.url ? (
          <a
            className="btn secondary small"
            href={item.url}
            target="_blank"
            rel="noreferrer"
            style={{ marginTop: 12 }}
          >
            View Details
          </a>
        ) : null}
      </div>
    </article>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} style={{ marginBottom: "2.25rem" }}>
      <h3 style={{ margin: "0 0 12px", textAlign: "center" }}>{title}</h3>
      {children}
    </section>
  );
}

function EmptyState({ children }) {
  return (
    <div className="ui-card" style={{ gridColumn: "1 / -1" }}>
      <p style={{ margin: 0 }}>{children}</p>
    </div>
  );
}

export default function Listings() {
  const [data, setData] = useState(() => readListingsSnapshot() || { active: [], sold: [] });
  const [status, setStatus] = useState(() => (readListingsSnapshot() ? "ready" : "loading"));

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const r = await fetch("/api/listings", { signal: controller.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();

        const next = {
          active: Array.isArray(j?.active) ? j.active : [],
          sold: Array.isArray(j?.sold) ? j.sold : [],
        };

        setData(next);
        setStatus("ready");
        writeListingsSnapshot(next);
      } catch (e) {
        // Keep showing snapshot data if we already painted it
        if (e?.name !== "AbortError") setStatus((s) => (s === "ready" ? s : "error"));
      }
    })();

    return () => controller.abort();
  }, []);

  const active = useMemo(() => data.active, [data.active]);
  const sold = useMemo(() => data.sold, [data.sold]);
const soldLoop = useMemo(() => [...sold, ...sold], [sold]);

  return (
    <div
      className={`interior-page listings-page ${
        brickFooter ? "has-hero-image" : ""
      }`}
    >
      <Navbar variant="listings" />

      <header className="listings-hero">
        {brickFooter ? (
          <div className="listings-hero-media" aria-hidden="true">
            <img src={brickFooter} alt="" />
            <div className="listings-hero-overlay" />
          </div>
        ) : (
          <div className="listings-hero-fallback" aria-hidden="true" />
        )}

        <div className="interior-inner listings-hero-content">
          <h2>My Portfolio</h2>
          <p
  style={{
    margin: "10px 0 0",
    color: "var(--text-on-dark)",
    textAlign: "center",
  }}
>
  A curated showcase of active listings and recent sales.
</p>
        </div>

        <div className="listings-hero-chips">
          <JumpChip href="#active">Active</JumpChip>
          <JumpChip href="#sold">Sold</JumpChip>
        </div>
      </header>

      <main
        className="interior-inner listings-main"
        style={{ padding: "2.25rem 0 4.25rem" }}
      >
        {status === "error" ? (
          <div className="ui-card">
            <h3 style={{ marginTop: 0 }}>Couldn’t load properties</h3>
            <p>Please try again in a moment.</p>
          </div>
        ) : (
          <>
            <Section id="active" title="Active Properties">
              <div className="card-grid listings-grid">
                {active.length ? (
                  active.map((x, i) => (
                    <ListingCard
                      key={x?.id || `${x?.address}-${x?.zip || ""}`}
                      item={x}
                      priority={i < 3}
                    />
                  ))
                ) : (
                  <EmptyState>
                    No active properties right now — check back soon.
                  </EmptyState>
                )}
              </div>
            </Section>

            <Section id="sold" title="Sold Listings">
  {sold.length ? (
    <div className="sold-marquee" aria-label="Sold listings scrolling carousel">
      <div
        className="sold-marquee-track"
        style={{
          // optional: speed scales a bit with number of items
          "--sold-speed": `${Math.max(40, sold.length * 4)}s`,
        }}
      >
        {soldLoop.map((x, idx) => (
          <ListingCard
            key={`${x?.id || `${x?.address}-${x?.zip || ""}`}-${idx}`}
            item={x}
          />
        ))}
      </div>
    </div>
  ) : (
    <EmptyState>No sold listings added yet.</EmptyState>
  )}

  <div style={{ display: "flex", justifyContent: "center", marginTop: 18 }}>
    <a
      className="btn secondary"
      href={ZILLOW_PROFILE_URL}
      target="_blank"
      rel="noreferrer"
    >
      View full sales history on Zillow
    </a>
  </div>
</Section>
          </>
        )}
      </main>
    </div>
  );
}